import { useFormikContext } from 'formik';
import type { ReactElement } from 'react';
import styled from 'styled-components';
import type { Installer } from '../../../model';

interface Props {
  name: string;
  nameSelect: string;
  error?: string;
  installers: Installer[];
  label: string;
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
  padding: 12px 13px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  background: rgba(8, 14, 30, 0.35);
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Check = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  color: rgb(240, 242, 245);
  white-space: nowrap;
  user-select: none;

  input {
    width: 16px;
    height: 16px;
    accent-color: rgb(78, 95, 253);
    cursor: pointer;
  }
`;

const SelectField = styled.div`
  position: relative;
  flex: 1 1 0%;
  min-width: 0;

  select {
    width: 100%;
    appearance: none;
    -webkit-appearance: none;
    background: rgba(8, 14, 30, 0.5);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    color: rgb(240, 242, 245);
    padding: 8px 30px 8px 11px;
    font-weight: 600;
    font-size: 13px;
    cursor: pointer;
  }

  select:disabled {
    opacity: 0.5;
    cursor: default;
  }

  select:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.2);
  }

  option {
    background: rgb(13, 23, 51);
    color: rgba(213, 221, 238, 0.92);
  }

  option:hover,
  option:checked {
    background: #1967d2;
    color: rgb(255, 255, 255);
  }

  .chev {
    position: absolute;
    right: 11px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    font-size: 11px;
    color: rgba(176, 186, 209, 0.6);
  }
`;

const Notes = styled.textarea`
  width: 100%;
  min-height: 62px;
  resize: vertical;
  background: rgba(8, 14, 30, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: rgba(213, 221, 238, 0.85);
  padding: 8px 11px;
  font-weight: 400;
  font-size: 12px;

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

const ErrorText = styled.div`
  font-weight: 500;
  font-size: 11px;
  color: rgb(255, 138, 147);
`;

export const InstallerControl = ({
  name,
  nameSelect,
  installers,
  label,
  error
}: Props): ReactElement => {
  const formik = useFormikContext<Record<string, unknown>>();
  const { values } = formik;
  const installer = values[nameSelect] as Installer | undefined;
  const isSelected = Boolean(values[name]);
  const notes = installer?.notes ?? '';

  return (
    <Wrap>
      <Head>
        <Check>
          <input
            type='checkbox'
            checked={isSelected}
            onChange={(event): void => {
              formik.setFieldValue(name, event.target.checked);
            }}
          />
          {label}
        </Check>

        <SelectField>
          <select
            disabled={!isSelected}
            value={installer?.id ?? ''}
            onChange={(event): void => {
              const selected = installers.find((item) => item.id === event.target.value);

              formik.setFieldValue(nameSelect, selected);
            }}
          >
            <option value=''>Version…</option>
            {installers.map((item) => (
              <option key={item.id} value={item.id}>
                {item.versionName}
              </option>
            ))}
          </select>
          <i className='fas fa-chevron-down chev' />
        </SelectField>
      </Head>

      {notes !== '' && <Notes readOnly value={notes} />}

      {error !== undefined && error !== '' && <ErrorText>{error}</ErrorText>}
    </Wrap>
  );
};
